/**
 * SES契約管理用の API hooks
 */
import { useCallback, useEffect, useState } from "react";
import { RestApiError, restClient } from "../lib/rest-client";

/**
 * SES契約アイテムの型
 */
export type SesContractItem = {
  id: string;
  talentId: string;
  pipelineId: string | null;
  previousContractId: string | null;
  clientCompany: string | null;
  projectName: string | null;
  contractType: string | null;
  reward: number | null;
  margin: number | null;
  startDate: string | null;
  endDate: string | null;
  autoRenewal: boolean;
  status: string;
  renewalAlertDays: number | null;
  renewalCount: number;
  remarks: string | null;
  createdAt: string;
  updatedAt: string;
};

/**
 * SES契約一覧レスポンスの型
 */
type SesContractListResponse = {
  items: SesContractItem[];
  total: number;
};

/**
 * タレントの契約一覧を取得する hook
 * @param {string | undefined} talentId タレントID
 * @return {object} contracts, total, loading, error, refetch
 */
export function useTalentContracts(talentId: string | undefined): {
  contracts: SesContractItem[];
  total: number;
  loading: boolean;
  error: RestApiError | null;
  refetch: () => Promise<void>;
} {
  const [contracts, setContracts] = useState<SesContractItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(!!talentId);
  const [error, setError] = useState<RestApiError | null>(null);

  const fetchContracts = useCallback(async () => {
    if (!talentId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await restClient.get<SesContractListResponse>(
        `/talents/${talentId}/contracts`,
      );
      setContracts(response.items);
      setTotal(response.total);
    } catch (e) {
      if (e instanceof RestApiError) {
        setError(e);
      } else {
        setError(
          new RestApiError(
            e instanceof Error ? e.message : "Unknown error",
            500,
          ),
        );
      }
    } finally {
      setLoading(false);
    }
  }, [talentId]);

  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  return { contracts, total, loading, error, refetch: fetchContracts };
}

/**
 * 契約を作成する hook
 * @return {object} createContract, loading, error
 */
export function useCreateContract(): {
  createContract: (
    talentId: string,
    data: Partial<SesContractItem>,
  ) => Promise<SesContractItem>;
  loading: boolean;
  error: RestApiError | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<RestApiError | null>(null);

  const createContract = useCallback(
    async (
      talentId: string,
      data: Partial<SesContractItem>,
    ): Promise<SesContractItem> => {
      setLoading(true);
      setError(null);

      try {
        const response = await restClient.post<SesContractItem>(
          `/talents/${talentId}/contracts`,
          data,
        );
        return response;
      } catch (e) {
        const apiError =
          e instanceof RestApiError
            ? e
            : new RestApiError(
                e instanceof Error ? e.message : "Unknown error",
                500,
              );
        setError(apiError);
        throw apiError;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { createContract, loading, error };
}

/**
 * 契約を更新する hook
 * @return {object} updateContract, loading, error
 */
export function useUpdateContract(): {
  updateContract: (
    talentId: string,
    contractId: string,
    data: Partial<SesContractItem>,
  ) => Promise<SesContractItem>;
  loading: boolean;
  error: RestApiError | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<RestApiError | null>(null);

  const updateContract = useCallback(
    async (
      talentId: string,
      contractId: string,
      data: Partial<SesContractItem>,
    ): Promise<SesContractItem> => {
      setLoading(true);
      setError(null);

      try {
        const response = await restClient.put<SesContractItem>(
          `/talents/${talentId}/contracts/${contractId}`,
          data,
        );
        return response;
      } catch (e) {
        const apiError =
          e instanceof RestApiError
            ? e
            : new RestApiError(
                e instanceof Error ? e.message : "Unknown error",
                500,
              );
        setError(apiError);
        throw apiError;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { updateContract, loading, error };
}

/**
 * 契約を更新（自動更新）する hook
 * @return {object} renewContract, loading, error
 */
export function useRenewContract(): {
  renewContract: (
    talentId: string,
    contractId: string,
    data?: Partial<SesContractItem>,
  ) => Promise<SesContractItem>;
  loading: boolean;
  error: RestApiError | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<RestApiError | null>(null);

  const renewContract = useCallback(
    async (
      talentId: string,
      contractId: string,
      data?: Partial<SesContractItem>,
    ): Promise<SesContractItem> => {
      setLoading(true);
      setError(null);

      try {
        const response = await restClient.post<SesContractItem>(
          `/talents/${talentId}/contracts/${contractId}/renew`,
          data,
        );
        return response;
      } catch (e) {
        const apiError =
          e instanceof RestApiError
            ? e
            : new RestApiError(
                e instanceof Error ? e.message : "Unknown error",
                500,
              );
        setError(apiError);
        throw apiError;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { renewContract, loading, error };
}
